import React, { useContext, useMemo, useState } from 'react';
import Styled from 'styled';
import classnames from 'classnames';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';

import SVGIcon from 'lib/images/svg-icon/svg-icon';

import { SiteContext } from 'four-nets';
import arrow from 'four-nets/icons/svg/icon_arrow-down.svg';

import categoryIcon_bride from 'four-nets/icons/svg/icon_category-bride.svg';
import categoryIcon_groom from 'four-nets/icons/svg/icon_category-groom.svg';
import categoryIcon_dress from 'four-nets/icons/svg/icon_category-wedding-dress.svg';
import categoryIcon_decorations from 'four-nets/icons/svg/icon_category-decorations.svg';
import categoryIcon_photoblogs from 'four-nets/icons/svg/icon_photoblogs.svg';
import categoryIcon_reconstrucion from 'four-nets/icons/svg/icon_category-reconstrucion.svg';
import categoryIcon_interior from 'four-nets/icons/svg/icon_category-interior.svg';
import categoryIcon_garden from 'four-nets/icons/svg/icon_category-garden.svg';
import categoryIcon_estate from 'four-nets/icons/svg/icon_category-real-estate.svg';
import categoryIcon_gardentechnology from 'four-nets/icons/svg/icon_category-garden-technology.svg';
import categoryIcon_others from 'four-nets/icons/svg/icon_category-others.svg';

import query, { ServerCategoriesQuery } from './server-categories.graphql';

import defaultStyles from './categories-panel-view.scss';
import categoryStyles from './categories-panel-category.scss';
import arrowStyles from './categories-panel-arrow.scss';

type Child = NonNullable<NonNullable<NonNullable<ServerCategoriesQuery['serverCategories']>[0]>['children']>[0];

const ICONS = {
    101: new Map([
        ['Nevesta, dámy a dievčatá', categoryIcon_bride],
        ['Ženích, páni a chlapci', categoryIcon_groom],
        ['Spoločenské oblečenie pre hostí', categoryIcon_dress],
        ['Svadobné dekorácie a predmety', categoryIcon_decorations],
        ['Svadobné služby', categoryIcon_photoblogs],
    ]),
    102: new Map([
        ['Nevěsta, dámy a děvčata', categoryIcon_bride],
        ['Ženich, pánové a chlapci', categoryIcon_groom],
        ['Společenské oblečení pro hosty', categoryIcon_dress],
        ['Svatební dekorace a předměty', categoryIcon_decorations],
        ['Svatební služby', categoryIcon_photoblogs],
    ]),
    301: new Map([
        ['Stavba a rekonštrukcia', categoryIcon_reconstrucion],
        ['Interiér a zariadenie', categoryIcon_interior],
        ['Záhrada a okolie domu', categoryIcon_garden],
        ['Reality', categoryIcon_estate],
    ]),
    302: new Map([
        ['Stavba a rekonstrukce', categoryIcon_reconstrucion],
        ['Interiér a zařízení', categoryIcon_interior],
        ['Zahrada a okolí domu', categoryIcon_garden],
        ['Reality - jen soukromá inzerce', categoryIcon_estate],
    ]),
    401: new Map([
        ['Záhrada', categoryIcon_garden],
        ['Reality, stavba, náradie', categoryIcon_estate],
        ['Dom a voľný čas', categoryIcon_gardentechnology],
        ['Všetko ostatné', categoryIcon_others],
    ]),
};

export interface Props {
    styles: typeof defaultStyles;
}

const CategoriesPanelView: React.FC<Props> = props => {
    const { styles } = props;

    const site = useContext(SiteContext);

    const [rootCategoryOpenIndex, setRootCategoryOpenIndex] = useState<number | null>(null);

    const { data } = useQuery<ServerCategoriesQuery>(query);

    const children = useMemo(() => {
        if (
            data?.serverCategories?.length &&
            rootCategoryOpenIndex != null &&
            data.serverCategories[rootCategoryOpenIndex]?.children
        ) {
            const _children = data.serverCategories[rootCategoryOpenIndex]!.children;
            return _children as Child[];
        }
        return [];
    }, [rootCategoryOpenIndex, data]);

    const iconMap = useMemo(() => ICONS[site.id], [site]);
    const headingOrder = useMemo(() => [styles.order1, styles.order3, styles.order5, styles.order7, styles.order9], [
        styles,
    ]);
    const listOrder = useMemo(() => [styles.order2, styles.order4, styles.order6, styles.order8, styles.order10], [
        styles,
    ]);

    const subcategories = (mediaClass: string) => {
        const { styles } = props;

        return rootCategoryOpenIndex !== null ? (
            <ul className={`${styles.list} ${listOrder[rootCategoryOpenIndex]} ${mediaClass}`}>
                {children.map(child => (
                    <li key={child!.name} className={styles.item}>
                        <Link to={child!.slug} className={styles.link}>
                            {child!.name}
                            <span className={styles.count}>{child!.productsCount}</span>
                        </Link>
                    </li>
                ))}
            </ul>
        ) : null;
    };
    const subcategoriesMobile = subcategories(styles.mobile);
    const subcategoriesDesktop = subcategories(styles.desktop);

    return data?.serverCategories?.length ? (
        <div className={styles.mainWrapper}>
            <div className={classnames(styles.diamond, { [styles.diamondGray]: rootCategoryOpenIndex !== 0 })} />
            <div className={styles.rowWrapper}>
                {data.serverCategories.map((parentCategory, index) => (
                    <h2 className={`${styles.heading} ${headingOrder[index]}`} key={parentCategory?.name}>
                        <button
                            className={`${styles.button} ${
                                index === rootCategoryOpenIndex ? styles.activeButton : null
                            }`}
                            onClick={() => {
                                setRootCategoryOpenIndex(prev => (prev === index ? null : index));
                            }}
                        >
                            <span className={styles.buttonContent}>
                                <SVGIcon
                                    style={{ image: categoryStyles.categoryIcon }}
                                    src={iconMap.get(parentCategory?.name as string)}
                                />
                                {parentCategory?.name}
                                {parentCategory?.productsCount ? (
                                    <span className={styles.titleCount}>{parentCategory.productsCount}</span>
                                ) : null}
                            </span>
                            <SVGIcon
                                style={{ image: arrowStyles.arrowIcon }}
                                src={arrow}
                                className={classnames({ [arrowStyles.arrowIconOpen]: rootCategoryOpenIndex === index })}
                            />
                        </button>
                    </h2>
                ))}
                {subcategoriesMobile}
            </div>
            {subcategoriesDesktop}
        </div>
    ) : null;
};

export default Styled(defaultStyles)(CategoriesPanelView);
